import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Space } from 'antd';
import { ReactComponent as ErrorSignIcon } from 'assets/images/icons/errorSignIcon.svg';
import { StyledCard } from 'components/Custom/card';
import { NumericInput } from 'components/Custom/inputs';
import { ListItemSpan, TableItemSpan } from 'components/Custom/spans';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BillingCheckout } from 'store/billing/models/billingCheckout';
import { CurrentSubscription } from 'store/billing/models/currentSubscription';

interface EditPlanProps {
  currentSubscriptionPlan: CurrentSubscription;
  updatePlan: (billing: BillingCheckout) => void;
  close: () => void;
}

export const EditPlan: React.FC<EditPlanProps> = ({
  currentSubscriptionPlan,
  updatePlan,
  close,
}) => {
  const { t } = useTranslation();
  const [additionalEntities, setAdditionalEntities] = React.useState<any>(
    currentSubscriptionPlan?.additionalOrganisations
  );
  const [vatNumberCheckerPerEntity, setVatNumberCheckerPerEntity] =
    React.useState(currentSubscriptionPlan?.minimumVatNumberChecker);

  return (
    <>
      <TableItemSpan fontSize="14px" $lineHeight="18px">
        {t('billing.edit-plan-desc')}
      </TableItemSpan>
      <br />
      <br />
      <TableItemSpan
        fontSize="14px"
        $lineHeight="22px"
        style={{ paddingTop: 20 }}
      >
        {t('billing.edit-plan-desc2')}
      </TableItemSpan>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexFlow: 'row',
          alignContent: 'normal',
          paddingBottom: '15px',
          paddingTop: '30px',
        }}
      >
        <ListItemSpan fontSize="14px" $lineHeight="40px" fontWeight="400">
          {t('billing.additional-entities')}
        </ListItemSpan>
        <NumericInput //@ts-ignore
          hasError={
            additionalEntities <
            currentSubscriptionPlan?.minimumAdditionalEntities
          }
          min={currentSubscriptionPlan?.minimumAdditionalEntities}
          addonAfter={
            <PlusOutlined
              disabled={additionalEntities >= 500}
              style={{ color: 'var(--color-primary, #41aaaf)' }}
              onClick={() => setAdditionalEntities(additionalEntities + 1)}
            />
          }
          addonBefore={
            <MinusOutlined
              disabled={additionalEntities <= 0}
              style={{ color: 'var(--color-primary, #41aaaf)' }}
              onClick={() => setAdditionalEntities(additionalEntities - 1)}
            />
          }
          controls={false}
          value={additionalEntities}
          onChange={(value) =>
            setAdditionalEntities(
              value === null || value === undefined ? 0 : value
            )
          }
        />
      </div>
      {additionalEntities <
        currentSubscriptionPlan?.minimumAdditionalEntities && (
        <StyledCard
          $stopshadowbox
          $padding="15px 24px"
          width="100%"
          style={{
            margin: '0px 0px 30px',
            backgroundColor: '#D1E8EA',
            border: '1px solid var(--color-border, #dbeae3)',
          }}
        >
          <Space style={{ columnGap: 16 }}>
            <ErrorSignIcon />
            <TableItemSpan fontSize="12px" $lineHeight="16px" fontWeight="400">
              You need to first delete one or more Entities from the Entities
              Section before reducing the amount.
            </TableItemSpan>
          </Space>
        </StyledCard>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexFlow: 'row',
          alignContent: 'normal',
          paddingBottom: '45px',
          paddingTop: '15px',
        }}
      >
        <ListItemSpan fontSize="14px" $lineHeight="40px" fontWeight="400">
          {t('navigation.vat-number-checker')}{' '}
        </ListItemSpan>
        <NumericInput //@ts-ignore
          hasError={
            vatNumberCheckerPerEntity <
            currentSubscriptionPlan?.minimumVatNumberChecker
          }
          min={currentSubscriptionPlan?.minimumVatNumberChecker}
          addonAfter={
            <PlusOutlined
              style={{ color: 'var(--color-primary, #41aaaf)' }}
              onClick={() =>
                setVatNumberCheckerPerEntity(vatNumberCheckerPerEntity + 1)
              }
            />
          }
          addonBefore={
            <MinusOutlined
              disabled={vatNumberCheckerPerEntity <= 0}
              style={{ color: 'var(--color-primary, #41aaaf)' }}
              onClick={() =>
                setVatNumberCheckerPerEntity(vatNumberCheckerPerEntity - 1)
              }
            />
          }
          controls={false}
          value={vatNumberCheckerPerEntity}
          onChange={(value: number) =>
            setVatNumberCheckerPerEntity(
              value === null || value === undefined ? 0 : value
            )
          }
        />
      </div>
      {vatNumberCheckerPerEntity <
        currentSubscriptionPlan?.minimumVatNumberChecker && (
        <StyledCard
          $stopshadowbox
          $padding="15px 24px"
          width="100%"
          style={{
            margin: '0px 0px 30px',
            backgroundColor: 'var(--color-layout-bg, #E9F6F7)',
            border: '1px solid var(--color-border, #dbeae3)',
          }}
        >
          <Space style={{ columnGap: 16 }}>
            <ErrorSignIcon />
            <TableItemSpan fontSize="12px" $lineHeight="16px" fontWeight="400">
              You need to first deactivate one or more VAT Number Checker
              entities from the{' '}
              <Link
                style={{ textDecoration: 'underline', color: 'var(--color-text-dark, #042a2a)' }}
                to={'/vies'}
                onClick={close}
              >
                VAT Number Checker
              </Link>{' '}
              Section before reducing the amount.
            </TableItemSpan>
          </Space>
        </StyledCard>
      )}
      <ScopeButton
        disabled={
          additionalEntities <
            currentSubscriptionPlan?.minimumAdditionalEntities ||
          vatNumberCheckerPerEntity <
            currentSubscriptionPlan?.minimumVatNumberChecker
        }
        type="primary"
        onClick={() =>
          updatePlan({
            typeId: currentSubscriptionPlan.typeId,
            licenseId: currentSubscriptionPlan.subscriptionId,
            additionalEntities,
            vatnumberchecker: vatNumberCheckerPerEntity,
            addressLine1: '',
            addressLine2: '',
            town: '',
            state: '',
            country: '',
            zipCode: '',
            vatNumber: '',
            currency: currentSubscriptionPlan.currency,
          })
        }
      >
        {t('billing.update-plan')}
      </ScopeButton>
    </>
  );
};
